import React from 'react';
import { StackItemShim as StackItem } from '@fluentui/react-migration-v8-v9';
import {
  ChatRegular,
  ArrowUndoRegular,
  SignatureRegular,
  EditRegular,
} from '@fluentui/react-icons';
import { useQuery } from '@apollo/client';
import { Divider, Label, Skeleton, SkeletonItem } from '@fluentui/react-components';
import ActivityItem from '../../common/ActivityItem';
import { dividerStyles, labelStyles } from '../../../app/common/styles/CommonStyleObjects';
import {
  GET_ALL_MILESTONE_HISTORY,
  MilestoneHistory,
  MilestoneHistoryActionType,
} from '../../../utils/statApi/MilestoneApi';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import { IMilestone } from './Interfaces';
import momentUtc from '../../../utils/DateFormatter';

interface IMilestoneHistoryProps {
  milestone: IMilestone;
}

const MilestoneHistoryList = ({ milestone }: IMilestoneHistoryProps): JSX.Element => {
  const styles = dividerStyles();
  const labelClasses = labelStyles();
  const milestoneId = milestone.id;
  const { error, loading, data } = useQuery(GET_ALL_MILESTONE_HISTORY, {
    variables: { milestoneId },
    fetchPolicy: 'network-only',
  });

  const mapActionType = (actionType: MilestoneHistoryActionType, comments: string): string => {
    const rescindString = 'rescinded this milestone';
    const commentString = 'commented';
    switch (actionType) {
      case MilestoneHistoryActionType.Signoff:
        return 'signed off on this milestone';
      case MilestoneHistoryActionType.Rescind:
        if (comments) {
          return `${rescindString} - ${comments}`;
        }
        return rescindString;
      case MilestoneHistoryActionType.Update:
        return `updated the due date on this milestone`;
      case MilestoneHistoryActionType.Comment:
        return `${commentString} - ${comments}`;
      default:
        return 'Unknown action!';
    }
  };

  const getActionIcon = (actionType: MilestoneHistoryActionType) => {
    switch (actionType) {
      case MilestoneHistoryActionType.Comment:
        return <ChatRegular />;
      case MilestoneHistoryActionType.Rescind:
        return <ArrowUndoRegular />;
      case MilestoneHistoryActionType.Signoff:
        return <SignatureRegular />;
      case MilestoneHistoryActionType.Update:
        return <EditRegular />;
      default:
        return null;
    }
  };
  return (
    <StackItem>
      <Divider className={styles.divider} />
      <div>
        <Label className={labelClasses.root}>Milestone History</Label>
        {loading && (
          <Skeleton aria-label="Loading milestone history">
            <SkeletonItem />
          </Skeleton>
        )}
        {!loading && (
          <>
            <LoadingErrorMessage
              error={error}
              loading={loading}
              actionName="while loading the milestone history"
            />
            {data &&
              data.milestoneHistory.map((ma: MilestoneHistory) => {
                return (
                  <ActivityItem
                    key={ma.id}
                    timeStamp={momentUtc(ma.actionDate)}
                    activityIcon={getActionIcon(ma.actionType)}
                    activityDescription={
                      <div>
                        {` - ${ma.statUserName} ${mapActionType(ma.actionType, ma.comments)}`}
                      </div>
                    }
                  />
                );
              })}
          </>
        )}
      </div>
    </StackItem>
  );
};

export default MilestoneHistoryList;
